import React, { useState } from 'react';
import Router from 'next/router';
import Link from 'next/link';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import domainModel from 'domain-model';
import _ from 'lodash';
import { Row, Col, Navbar, Nav, NavbarToggler } from 'reactstrap';
import Image from 'flaivy-react/components/Image';
import StyledButton from 'flaivy-react/components/StyledButton';
import UserOffCanvas from './UserOffCanvas';
import LoginModal from '../LoginModal';
import MobileOffCanvas from './MobileOffCanvas';
import useHamburgerMenu from './hamburgerMenuHook';
import OnlyOnSmallScreen from './OnlyOnSmallScreen';

import SearchInput from './SearchInput';
import PageNavigation from './PageNavigation';

import IconHambugerMenu from '../icons/IconHamburgerMenu';
import CartButton from './CartButton';

import { toggleLoginModal as _toggleLoginModal } from '../../shared/actions/login-modal.action';
import MyFavoritesButton from './MyFavoritesButton';
import UserButton from './UserButton';

const { isPrivabShop } = domainModel.shopConfig;

const Logo = ({ shopConfig, className }) => {
  const { logo, organization } = shopConfig;
  return (
    <>
      <Link href="/" className={`${className} d-inline-block me-2 py-2`}>
        <Image
          src={logo}
          alt={`${organization.name} Shop logo`}
          resX={175}
          resY={70}
          style={{ height: 'auto', maxWidth: '90%' }}
        />
      </Link>
    </>
  );
};

Logo.propTypes = {
  shopConfig: PropTypes.object.isRequired,
  className: PropTypes.string,
};
Logo.defaultProps = {
  className: '',
};

const LogInButton = ({ authentication, sessionUser, toggleLoginModal }) => {
  const isLoggedIn = !_.isEmpty(sessionUser);
  const isAuthenticated = authentication.isAuthenticated;

  const [isUserOffCanvasOpen, setIsUserOffCanvasOpen] = useState(false);
  const toggleUserOffCanvas = () => {
    setIsUserOffCanvasOpen(!isUserOffCanvasOpen);
  };
  return (
    <>
      {!isAuthenticated && (
        <StyledButton
          id="LogInButtonNavBar"
          onClick={isLoggedIn ? toggleUserOffCanvas : toggleLoginModal}
          className="ms-3"
          color="light"
        >
          Logga in
        </StyledButton>
      )}

      {!isLoggedIn && <LoginModal />}
      {isLoggedIn && (
        <UserOffCanvas
          isOpen={isUserOffCanvasOpen}
          toggle={toggleUserOffCanvas}
        />
      )}
    </>
  );
};

LogInButton.propTypes = {
  sessionUser: PropTypes.object,
  toggleLoginModal: PropTypes.func.isRequired,
  authentication: PropTypes.object,
};
LogInButton.defaultProps = {
  sessionUser: null,
};

const BecomeCustomerBtn = ({ authentication }) => {
  const isAuthenticated = authentication.isAuthenticated;
  return (
    <>
      {!isAuthenticated && (
        <StyledButton
          color="light"
          className="p-1 p-lg-2"
          id="BecomeCustomerButtonNavBar"
          onClick={() => Router.push('/register')}
        >
          Bli kund
        </StyledButton>
      )}
    </>
  );
};

BecomeCustomerBtn.propTypes = {
  authentication: PropTypes.object.isRequired,
};

function NavBar({
  asPath,
  authentication,
  shopConfig,
  isAtTopOfPage,
  toggleLoginModal,
}) {
  const [isSearchbarFocused, setIsSearchbarFocused] = useState(false);
  const { isHamburgerMenuOpen, toggleHamburgerMenu } = useHamburgerMenu();

  const isAuthenticated = authentication.isAuthenticated;
  const closeFocus = () => {
    setIsSearchbarFocused(false);
  };

  return (
    <>
      <Navbar
        expand="lg"
        container="xxl"
        className={`px-0 d-print-none sticky-top navbar-version-1 ${
          isAtTopOfPage ? 'is-at-top-of-page' : ''
        }`}
        id="navbar"
      >
        {isSearchbarFocused && (
          <div
            style={{
              background: 'rgba(0,0,0,0.3)',
              width: '100vw',
              height: '170vh',
              zIndex: '1030',
              top: 0,
              left: 0,
              position: 'absolute',
            }}
            onClick={() => setIsSearchbarFocused(false)}
          ></div>
        )}
        <div className="d-flex flex-column align-items-start w-100">
          <Row className="g-0 w-100">
            <Col
              xs={6}
              lg={12}
              className="d-flex flex-row justify-content-between"
            >
              <ConnectedLogo />
              <div className="d-none d-lg-flex align-items-center">
                <div
                  style={
                    isSearchbarFocused
                      ? {
                          minWidth: '30rem',
                          zIndex: '5000',
                        }
                      : { minWidth: '30rem' }
                  }
                  onClick={() => setIsSearchbarFocused(true)}
                >
                  <SearchInput
                    closeFocus={closeFocus}
                    shopConfig={shopConfig}
                  />
                </div>
              </div>
              <div className="d-none d-lg-flex align-items-center pe-4">
                {!isAuthenticated && (
                  <>
                    <ConnectedBecomeCustomerBtn />
                    <ConnectedLogInButton />
                  </>
                )}
                <MyFavoritesButton />
                <CartButton bootstrapBreakpoint="lg" />
                <UserButton toggleLoginModal={toggleLoginModal} />
              </div>
            </Col>
            <Col
              xs={6}
              className="text-end d-flex align-items-center align-self-center justify-content-end d-lg-none"
            >
              {!isAuthenticated && <ConnectedBecomeCustomerBtn />}

              <Nav className="d-flex align-items-center flex-row" navbar>
                <MyFavoritesButton />
                <CartButton bootstrapBreakpoint="xs" />

                <NavbarToggler
                  onClick={toggleHamburgerMenu}
                  className="ps-2 ms-1 ms-lg-0"
                >
                  {isHamburgerMenuOpen ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      fill="currentColor"
                      className="bi bi-x-lg"
                      viewBox="0 0 16 16"
                    >
                      <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                    </svg>
                  ) : (
                    <IconHambugerMenu
                      stroke={isPrivabShop(shopConfig) ? 'white' : 'black'}
                    />
                  )}
                </NavbarToggler>
              </Nav>
            </Col>
          </Row>
          <Row className="w-100 d-lg-none pb-2 ps-2">
            <Col sm={{ offset: 1, size: 10 }}>
              <div
                className="position-relative"
                style={
                  isSearchbarFocused
                    ? {
                        zIndex: '5000',
                      }
                    : {}
                }
                onTouchStart={() => setIsSearchbarFocused(true)}
              >
                <SearchInput closeFocus={closeFocus} shopConfig={shopConfig} />
              </div>
            </Col>
          </Row>
          <Nav
            className="d-flex flex-lg-row justify-content-center align-items-center w-100"
            navbar
          >
            <div className="d-none d-lg-flex flex-lg-row align-items-center w-100">
              <PageNavigation
                toggleHamburgerMenu={toggleHamburgerMenu}
                isHamburgerMenuOpen={isHamburgerMenuOpen}
                asPath={asPath}
              />
            </div>
          </Nav>
        </div>
        <OnlyOnSmallScreen>
          <MobileOffCanvas
            isOpen={isHamburgerMenuOpen}
            toggle={toggleHamburgerMenu}
            asPath={asPath}
          />
        </OnlyOnSmallScreen>
      </Navbar>
    </>
  );
}

const mapStateToProps = ({
  shopConfig,
  authentication,
  persistedMultiCart,
  myFavoriteArticles,
}) => ({
  shopConfig,
  sessionUser: authentication.sessionUser,
  authentication,
  persistedMultiCart,
  myFavoriteArticles,
});

const mapDispatchToProps = {
  toggleLoginModal: _toggleLoginModal,
};
NavBar.propTypes = {
  asPath: PropTypes.string.isRequired,
  authentication: PropTypes.object.isRequired,
  shopConfig: PropTypes.object.isRequired,
};
const ConnectedBecomeCustomerBtn = connect(mapStateToProps)(BecomeCustomerBtn);
const ConnectedLogo = connect(mapStateToProps)(Logo);

const ConnectedLogInButton = connect(
  mapStateToProps,
  mapDispatchToProps,
)(LogInButton);
export default connect(mapStateToProps, mapDispatchToProps)(NavBar);
